// Tab "Phân loại khách hàng": hạng thành viên theo chi tiêu, phân khúc RFM, nhu cầu, phân loại tay có lý do + lịch sử, quy tắc phân loại
import { useState } from 'react';
import { DatePicker, Input, InputNumber, Modal, Select, message } from 'antd';
import dayjs from 'dayjs';
import { Info, PencilLine, Settings2, Undo2 } from 'lucide-react';
import { useTreatmentAuth } from '../Treatment/actions';
import { fmtDateTime, money, moneyShort } from '../Treatment/ui';
import { TIERS, type Customer360Config, type SegmentKey, type Tier } from './data';
import { SEGMENT, SEGMENT_KEYS } from './logic';
import useC360Store from './store';
import { Btn, Section, SegmentBadge, TierBadge } from './ui360';
import type { C360 } from './useCustomer360';

const R_TEXT = ['Chưa đến', '> ngưỡng ngủ đông', '≤ ngưỡng ngủ đông', '≤ ngưỡng nguy cơ', '≤ 60 ngày', '≤ 30 ngày'];

export default function ClassPanel({ c }: { c: C360 }) {
  const { cls, metrics: m, cfg } = c;
  const logs = useC360Store(s => s.classLogs[c.key]) || [];
  const setOverride = useC360Store(s => s.setOverride);
  const { can, userName } = useTreatmentAuth();
  const [editing, setEditing] = useState(false);
  const [rules, setRules] = useState(false);

  const undo = () => Modal.confirm({
    title: 'Gỡ phân loại tay?', content: `Khách sẽ quay về hạng ${cls.autoTier} · ${SEGMENT[cls.autoSegment].label} (tự động).`, okText: 'Gỡ', cancelText: 'Không',
    onOk: () => setOverride(c.key, null, userName),
  });

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Hạng thành viên" extra={<>
          {can('approve') && <Btn onClick={() => setEditing(true)}><PencilLine className="h-3.5 w-3.5" /> Phân loại tay</Btn>}
          {cls.override && can('approve') && <Btn onClick={undo}><Undo2 className="h-3.5 w-3.5" /> Về tự động</Btn>}
        </>}>
          <div className="flex items-center gap-3">
            <TierBadge tier={cls.tier} manual={!!cls.override?.tier} size="lg" />
            {cls.override?.tier && <span className="text-xs text-slate-500">Tự động: {cls.autoTier}</span>}
          </div>
          <div className="mt-3 text-sm text-slate-600">Chi tiêu {cfg.windowMonths} tháng gần nhất: <b className="text-slate-800">{money(m.spendWindow)}đ</b></div>
          {cls.next ? (
            <div className="mt-2">
              <div className="h-2.5 rounded-full bg-slate-100"><div className="h-2.5 rounded-full bg-[#3e91ab]" style={{ width: `${Math.round(cls.next.pct * 100)}%` }} /></div>
              <div className="mt-1 text-xs text-slate-500">Còn <b>{money(cls.next.need)}đ</b> để lên hạng <b>{cls.next.tier}</b></div>
            </div>
          ) : <div className="mt-2 text-xs font-semibold text-indigo-700">Đã đạt hạng cao nhất</div>}
          <table className="mt-4 w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-slate-400"><th className="py-1">Hạng</th><th className="py-1">Chi tiêu từ</th><th className="py-1">Quyền lợi</th></tr></thead>
            <tbody>
              {cfg.tierRules.map(r => (
                <tr key={r.tier} className={`border-t border-slate-100 ${r.tier === cls.tier ? 'bg-[#ecf6f9]' : ''}`}>
                  <td className="py-1.5 pr-2"><TierBadge tier={r.tier} /></td>
                  <td className="py-1.5 pr-2 whitespace-nowrap">{moneyShort(r.minSpend)}</td>
                  <td className="py-1.5 text-xs text-slate-600">{r.benefit}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {cls.override && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              * Phân loại tay bởi <b>{cls.override.by}</b> lúc {fmtDateTime(cls.override.at)}{cls.override.until ? ` · hiệu lực đến ${dayjs(cls.override.until).format('DD/MM/YYYY')}` : ''}: {cls.override.reason}
            </div>
          )}
        </Section>

        <Section title="Phân khúc hành vi (RFM)" extra={can('approve') && <Btn onClick={() => setRules(true)}><Settings2 className="h-3.5 w-3.5" /> Quy tắc</Btn>}>
          <div className="flex items-center gap-2">
            <SegmentBadge segment={cls.segment} manual={!!cls.override?.segment} />
            {cls.override?.segment && <span className="text-xs text-slate-500">Tự động: {SEGMENT[cls.autoSegment].label}</span>}
          </div>
          <p className="mt-2 rounded-lg bg-slate-50 p-2 text-sm text-slate-700"><b>Gợi ý chăm sóc:</b> {SEGMENT[cls.segment].advice}</p>
          <div className="mt-3 space-y-2.5">
            <Score label="R – Gần đây" v={cls.rfm.r} text={m.lastVisit ? `${m.daysSinceLast} ngày từ lần đến cuối (${R_TEXT[cls.rfm.r]})` : 'Chưa đến lần nào'} />
            <Score label="F – Tần suất" v={cls.rfm.f} text={`${m.visitsWindow} lần đến trong ${cfg.windowMonths} tháng · tổng ${m.visits}`} />
            <Score label="M – Chi tiêu" v={cls.rfm.m} text={`${money(m.spendWindow)}đ trong ${cfg.windowMonths} tháng`} />
          </div>
          <div className="mt-4 text-xs text-slate-500">
            <div className="mb-1 flex items-center gap-1 font-semibold text-slate-600"><Info className="h-3.5 w-3.5" /> Cách xếp nhóm (theo thứ tự ưu tiên)</div>
            Chưa mua → <i>Chưa phát sinh</i> · ≥ {cfg.lostDays} ngày không đến → <i>Ngủ đông</i> · ≥ {cfg.atRiskDays} ngày → <i>Nguy cơ rời bỏ</i> · lần đầu ≤ {cfg.newDays} ngày → <i>Khách mới</i> · hạng Kim cương hoặc R/F/M cao → <i>VIP</i> · ≥ {cfg.loyalVisits} lần/kỳ → <i>Trung thành</i> · ≥ {cfg.careDays} ngày → <i>Cần chăm sóc</i> · còn lại → <i>Tiềm năng</i>.
          </div>
        </Section>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Nhu cầu dịch vụ">
          {c.interests.length ? (
            <div className="space-y-2">
              {c.interests.map(x => {
                const max = c.interests[0].n;
                return (
                  <div key={x.group} className="text-sm">
                    <div className="flex justify-between"><span>{x.group}</span><span className="text-slate-500">{x.n}</span></div>
                    <div className="h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-[#3e91ab]" style={{ width: `${(x.n / max) * 100}%` }} /></div>
                  </div>
                );
              })}
            </div>
          ) : <div className="py-4 text-center text-sm text-slate-400">Chưa có dữ liệu mua</div>}
        </Section>
        <Section title="Lịch sử phân loại">
          {logs.length ? (
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {logs.map((l, i) => (
                <div key={i} className="border-l-2 border-[#3e91ab] pl-2 text-sm">
                  <div className="text-slate-700">{l.text}</div>
                  <div className="text-xs text-slate-400">{l.by} · {fmtDateTime(l.at)}</div>
                </div>
              ))}
            </div>
          ) : <div className="py-4 text-center text-sm text-slate-400">Chưa có thay đổi – đang phân loại tự động</div>}
        </Section>
      </div>

      <OverrideModal open={editing} c={c} onClose={() => setEditing(false)} />
      <RulesModal open={rules} onClose={() => setRules(false)} />
    </div>
  );
}

function Score({ label, v, text }: { label: string; v: number; text: string }) {
  return (
    <div className="text-sm">
      <div className="flex items-center justify-between"><span className="font-medium text-slate-700">{label}</span><span className="font-bold">{v}/5</span></div>
      <div className="mt-0.5 flex gap-1">{[1, 2, 3, 4, 5].map(i => <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= v ? 'bg-[#3e91ab]' : 'bg-slate-100'}`} />)}</div>
      <div className="mt-0.5 text-xs text-slate-500">{text}</div>
    </div>
  );
}

function OverrideModal({ open, c, onClose }: { open: boolean; c: C360; onClose: () => void }) {
  const setOverride = useC360Store(s => s.setOverride);
  const { userName } = useTreatmentAuth();
  const [tier, setTier] = useState<Tier | ''>('');
  const [segment, setSegment] = useState<SegmentKey | ''>('');
  const [until, setUntil] = useState<string>('');
  const [reason, setReason] = useState('');
  const valid = (tier || segment) && reason.trim().length >= 5;
  const save = () => {
    if (!valid) return;
    setOverride(c.key, { tier: tier || undefined, segment: segment || undefined, until: until || undefined, reason: reason.trim(), by: userName, at: new Date().toISOString() }, userName);
    message.success('Đã cập nhật phân loại');
    onClose();
  };
  return (
    <Modal open={open} onCancel={onClose} title="Phân loại tay" centered destroyOnHidden okText="Lưu" cancelText="Hủy" onOk={save} okButtonProps={{ disabled: !valid }}
      afterOpenChange={o => { if (o) { setTier(c.cls.override?.tier || ''); setSegment(c.cls.override?.segment || ''); setUntil(c.cls.override?.until || ''); setReason(''); } }}>
      <div className="space-y-3 text-sm">
        <p className="text-xs text-slate-500">Ghi đè kết quả tự động (VD: khách người nhà chủ, KOL, khách khiếu nại). Mọi thay đổi được ghi lịch sử.</p>
        <label className="block"><span className="mb-1 block font-medium">Hạng</span>
          <Select className="w-full" value={tier} onChange={setTier} options={[{ value: '', label: `Tự động (${c.cls.autoTier})` }, ...TIERS.map(t => ({ value: t, label: t }))]} /></label>
        <label className="block"><span className="mb-1 block font-medium">Phân khúc</span>
          <Select className="w-full" value={segment} onChange={setSegment} options={[{ value: '', label: `Tự động (${SEGMENT[c.cls.autoSegment].label})` }, ...SEGMENT_KEYS.map(k => ({ value: k, label: SEGMENT[k].label }))]} /></label>
        <label className="block"><span className="mb-1 block font-medium">Hiệu lực đến (bỏ trống = đến khi gỡ)</span>
          <DatePicker className="w-full" format="DD/MM/YYYY" value={until ? dayjs(until) : null} onChange={d => setUntil(d ? d.format('YYYY-MM-DD') : '')} disabledDate={d => d.isBefore(dayjs(), 'day')} /></label>
        <label className="block"><span className="mb-1 block font-medium">Lý do <span className="text-red-500">*</span></span>
          <Input.TextArea rows={2} maxLength={200} value={reason} onChange={e => setReason(e.target.value)} placeholder="Tối thiểu 5 ký tự" /></label>
      </div>
    </Modal>
  );
}

function RulesModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const cfg = useC360Store(s => s.config);
  const setConfig = useC360Store(s => s.setConfig);
  const { userName } = useTreatmentAuth();
  const [draft, setDraft] = useState<Customer360Config>(cfg);
  const num = (k: keyof Customer360Config) => (v: number | null) => setDraft(d => ({ ...d, [k]: Number(v) || 0 }));
  const sorted = draft.tierRules.every((r, i, a) => i === 0 || r.minSpend > a[i - 1].minSpend);
  const daysOk = draft.careDays < draft.atRiskDays && draft.atRiskDays < draft.lostDays;
  return (
    <Modal open={open} onCancel={onClose} title="Quy tắc phân loại khách hàng" centered width={640} destroyOnHidden okText="Lưu quy tắc" cancelText="Hủy"
      afterOpenChange={o => o && setDraft(cfg)} okButtonProps={{ disabled: !sorted || !daysOk }}
      onOk={() => { setConfig(draft, userName); message.success('Đã lưu – phân loại tự động tính lại cho mọi khách'); onClose(); }}>
      <div className="space-y-4 text-sm">
        <div>
          <div className="mb-1 font-semibold">Hạng theo chi tiêu thực trả trong <InputNumber size="small" min={1} max={36} value={draft.windowMonths} onChange={num('windowMonths')} /> tháng</div>
          <div className="space-y-2">
            {draft.tierRules.map((r, i) => (
              <div key={r.tier} className="grid grid-cols-[100px_150px_1fr] items-center gap-2">
                <TierBadge tier={r.tier} />
                <InputNumber className="!w-full" min={0} step={1_000_000} disabled={i === 0} value={r.minSpend}
                  formatter={v => new Intl.NumberFormat('vi-VN').format(Number(v || 0))} parser={v => Number(String(v).replace(/\D/g, ''))}
                  onChange={v => setDraft(d => ({ ...d, tierRules: d.tierRules.map((x, j) => (j === i ? { ...x, minSpend: Number(v) || 0 } : x)) }))} />
                <Input value={r.benefit} onChange={e => setDraft(d => ({ ...d, tierRules: d.tierRules.map((x, j) => (j === i ? { ...x, benefit: e.target.value } : x)) }))} />
              </div>
            ))}
          </div>
          {!sorted && <div className="mt-1 text-xs text-red-500">Ngưỡng hạng sau phải lớn hơn hạng trước</div>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label>Khách mới: lần đầu trong (ngày)<InputNumber className="!w-full" min={1} value={draft.newDays} onChange={num('newDays')} /></label>
          <label>Trung thành: số lần đến / kỳ từ<InputNumber className="!w-full" min={1} value={draft.loyalVisits} onChange={num('loyalVisits')} /></label>
          <label>Cần chăm sóc sau (ngày)<InputNumber className="!w-full" min={1} value={draft.careDays} onChange={num('careDays')} /></label>
          <label>Nguy cơ rời bỏ sau (ngày)<InputNumber className="!w-full" min={1} value={draft.atRiskDays} onChange={num('atRiskDays')} /></label>
          <label>Ngủ đông sau (ngày)<InputNumber className="!w-full" min={1} value={draft.lostDays} onChange={num('lostDays')} /></label>
          <label>Áp dụng ký xác nhận lần đến từ ngày<DatePicker className="!w-full" format="DD/MM/YYYY" value={draft.visitSignSince ? dayjs(draft.visitSignSince) : null} onChange={d => setDraft(x => ({ ...x, visitSignSince: d ? d.format('YYYY-MM-DD') : '' }))} /></label>
          <label>Lần đến chưa ký báo quản lý sau (ngày)<InputNumber className="!w-full" min={0} value={draft.visitSignDays} onChange={num('visitSignDays')} /></label>
        </div>
        {!daysOk && <div className="text-xs text-red-500">Cần: chăm sóc &lt; nguy cơ &lt; ngủ đông</div>}
      </div>
    </Modal>
  );
}
